import {useEffect} from 'react';
import {Routes, Route, Navigate, Outlet, useParams, useNavigate} from 'react-router-dom';
import {Layout, NotFound} from 'components';
import {usePersistentStore} from 'stores';
import {
  PageResetPassword,
  PageAcceptInvite,
  PageSignIn,
  PageSignUp,
  PageHome,
  PageProjects,
  PageTeam,
  PageAssets,
  PageAnalytics,
  PageProjectsUpdate,
  PageBilling,
  PageSettings,
  PagePaymentCards,
  PagePlans,
  PageHelp,
  PageImpersonate,
} from 'pages';

interface IRoutersProps {}

const Routers = ({}: IRoutersProps) => {
  const authToken = usePersistentStore(s => s.authToken);
  return (
    <Routes>
      <Route path="/reset-password" element={<PageResetPassword />} />
      <Route path="/accept-invite" element={<PageAcceptInvite />} />
      <Route path="/impersonate" element={<PageImpersonate />} />
      {authToken ? (
        <Route element={<PageMain />}>
          <Route path="/:workspace/billing/plans" element={<PagePlans />} />
          <Route path="/:workspace/billing/payment-cards" element={<PagePaymentCards />} />
          <Route path="/:workspace/:resource/:uid" element={<PageShowSelector />} />
          <Route path="/:workspace/:resource" element={<PageSelector />} />
          <Route path="/:workspace" element={<Navigate to="/me/home" />} />
          <Route path="/" element={<Navigate to="/me/home" />} />
        </Route>
      ) : (
        <Route element={<PageAuth />}>
          <Route path="/sign-in" element={<PageSignIn />} />
          <Route path="/sign-up" element={<PageSignUp />} />
          <Route path="/*" element={<PageRemember />} />
        </Route>
      )}
      <Route path="/*" element={<NotFound />} />
    </Routes>
  );
};

export default Routers;

const PageMain = () => {
  const params = useParams();
  const isProjectUpdatePath = params.resource === 'projects' && !!params.uid;
  const navigate = useNavigate();
  const urlAfterAuth = usePersistentStore(s => s.urlAfterAuth);
  const setUrlAfterAuth = usePersistentStore(s => s.setUrlAfterAuth);
  useEffect(() => {
    if (urlAfterAuth) {
      setUrlAfterAuth('');
      navigate(urlAfterAuth, {replace: true});
    }
  }, [urlAfterAuth]);
  return (
    <Layout hidden={isProjectUpdatePath}>
      <Outlet />
    </Layout>
  );
};

const PageSelector = () => {
  const resources = {
    home: <PageHome />,
    projects: <PageProjects />,
    assets: <PageAssets />,
    team: <PageTeam />,
    help: <PageHelp />,
    analytics: <PageAnalytics />,
    billing: <PageBilling />,
    settings: <PageSettings />,
  };
  const params = useParams();
  const resource = resources[params.resource as keyof typeof resources];
  return resource ?? <Navigate to="/me/home" />;
};

const PageShowSelector = () => {
  const resources = {
    home: <PageHome />,
    projects: <PageProjectsUpdate />,
    assets: <PageAssets />,
    team: <PageTeam />,
    analytics: <PageAnalytics />,
  };
  const params = useParams();
  const resource = resources[params.resource as keyof typeof resources];
  return resource ?? <Navigate to="/me/home" />;
};

const PageAuth = () => {
  useEffect(() => {
    const controller = new AbortController();
    window.addEventListener(
      'visibilitychange',
      async () => {
        if (document.visibilityState === 'visible') {
          usePersistentStore.persist.rehydrate();
        }
      },
      {signal: controller.signal},
    );
    return () => {
      controller.abort();
    };
  }, []);
  return <Outlet />;
};

const PageRemember = () => {
  const navigate = useNavigate();
  const setUrlAfterAuth = usePersistentStore(s => s.setUrlAfterAuth);
  useEffect(() => {
    (async () => {
      setUrlAfterAuth(location.pathname + location.search);
      await new Promise(resolve => setTimeout(resolve, 0));
      navigate('/sign-in', {replace: true});
    })();
  }, []);
  return <div className="w-screen h-screen"></div>;
};
